import {
    resourceIdZodSchema,
    type ResourceId,
} from "@@/shared/acquire/resources/resource-id";
import type {
    ResourceReservationId} from "@@/shared/acquire/resources/resource-reservation-id";
import {
    resourceReservationIdZodSchema,
} from "@@/shared/acquire/resources/resource-reservation-id";
import type {
    BookableSessionId} from "@@/shared/acquire/sessions/bookable-session-id";
import {
    bookableSessionIdZodSchema,
} from "@@/shared/acquire/sessions/bookable-session-id";
import type { OrderId} from "@@/shared/orders/order-id";
import { orderIdZodSchema } from "@@/shared/orders/order-id";
import {
    providerIdZodSchema,
    type ProviderId,
} from "@@/shared/providers/provider-id";
import { userIdZodSchema, type UserId } from "@@/shared/users/user-id";
import { z, type ZodSchema } from "zod";

type StatusReferences = {
    resourceId?: ResourceId;
    resourceReservationId?: ResourceReservationId;
    orderId?: OrderId;
    userId?: UserId;
    providerId: ProviderId;
    sessionId?: BookableSessionId;
};

const statusReferencesZodObject = z.object({
    resourceId: resourceIdZodSchema.optional(),
    resourceReservationId: resourceReservationIdZodSchema.optional(),
    orderId: orderIdZodSchema.optional(),
    userId: userIdZodSchema.optional(),
    providerId: providerIdZodSchema,
    sessionId: bookableSessionIdZodSchema.optional(),
}) satisfies ZodSchema<StatusReferences>;

/**
 * Checks if the provided `input` matches the `matches` object based on the required values. \
 * Only makes sure all keys with values in the input matches. \
 * If the input has no required values, it will always return `true`.
 *
 * @param input - The input object to be checked.
 * @param matches - The object to match against.
 * @returns `true` if the `input` matches the `matches` object based on the required values, `false` otherwise.
 */
const isStatusReferenceMatch = (
    input: StatusReferences,
    matches: StatusReferences,
) => {
    const requiredValues = Object.entries(input).filter(([_, value]) => {
        return typeof value !== "undefined";
    }) as [keyof StatusReferences, string][];

    if (!requiredValues.length) return true;
    try {
        const isMatch = requiredValues.every(([key, value]) => {
            return matches[key] === value;
        });
        return isMatch;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error, requiredValues, matches, input);
        throw error;
    }
};

/**
 * Checks if two `StatusReferences` objects are equal. \
 * All key values must match, even undefined values.
 *
 * @param references - The first `StatusReferences` object.
 * @param other - The second `StatusReferences` object.
 * @returns `true` if the two objects are equal, `false` otherwise.
 */
const isStatusReferencesEqual = (
    references: StatusReferences,
    other: StatusReferences,
) => {
    const check = (
        value: string | undefined,
        otherValue: string | undefined,
    ) => {
        if (typeof value === "undefined") {
            return typeof otherValue === "undefined";
        }
        return value === otherValue;
    };
    return (
        check(references.resourceId, other.resourceId) &&
        check(references.resourceReservationId, other.resourceReservationId) &&
        check(references.orderId, other.orderId) &&
        check(references.userId, other.userId) &&
        check(references.sessionId, other.sessionId) &&
        check(references.providerId, other.providerId)
    );
};

export {
    isStatusReferenceMatch,
    isStatusReferencesEqual,
    statusReferencesZodObject,
};
export type { StatusReferences };
